import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

// Blood-test preparation checklist shown as its own booking step
// (between "Details" and "Payment"). Adapted from the standalone
// marketing HTML into the app's MUI + theme-token styling so it sits
// natively inside the Booking stepper.
//
// Controlled acknowledgement: the parent owns the `acknowledged` flag
// and gates its own "Continue" button on it — this component just
// renders the timeline, the advisory strip and the ack checkbox.

const PHASES = [
  {
    node: { big: '72', small: 'HOURS' },
    label: 'Three days before',
    sub: 'Start here so alcohol fully clears your system.',
    items: [
      {
        title: 'No alcohol',
        desc: 'Skip all alcohol for the full 72 hours before your test.',
        why: 'Alcohol raises blood fats, sugar and liver markers, and takes days to clear.',
        marker: 'e.g. GGT',
      },
    ],
  },
  {
    node: { big: '24', small: 'HOURS' },
    label: 'The day before',
    sub: 'Ease off the body so markers settle.',
    items: [
      {
        title: 'No intense exercise',
        desc: 'Avoid heavy workouts for 24 hours before the draw.',
        why: 'Hard exercise temporarily spikes muscle enzymes and can shift sugar and other readings.',
        marker: 'e.g. CK (CPK)',
      },
    ],
  },
  {
    node: { big: 'PM', small: 'NIGHT' },
    label: 'The night before',
    sub: 'Set up your fast and rest well.',
    items: [
      {
        title: 'Begin a 12-hour fast',
        desc: 'No food or calorie-containing drinks for 12 hours before the test.',
        why: 'Food raises blood sugar and fats; fasting gives a true baseline for these results.',
        marker: 'e.g. Triglycerides',
      },
      {
        title: 'Sleep 7–8 hours',
        desc: "A good night's rest steadies your readings.",
        why: 'Poor sleep raises stress hormones and blood sugar, which can nudge results off.',
        marker: 'e.g. Cortisol',
      },
    ],
  },
  {
    node: { big: 'AM', small: 'TEST DAY' },
    label: 'The morning of',
    sub: 'Keep it plain and simple.',
    items: [
      {
        title: 'Drink plain water',
        desc: 'Staying hydrated makes the draw easier — water is fine.',
        why: 'Hydration plumps your veins for an easier draw and keeps readings from concentrating.',
        marker: 'e.g. Hematocrit (PCV)',
      },
      {
        title: 'No exercise this morning',
        desc: 'Rest until your sample is taken.',
        why: 'Even light morning exertion can briefly move several markers before the draw.',
        marker: 'e.g. Potassium',
      },
      {
        title: 'Skip vitamins & supplements',
        desc: 'Hold these until after your test.',
        why: 'Some supplements — biotin in particular — interfere with lab assays and skew results.',
        marker: 'e.g. TSH (thyroid)',
      },
      {
        title: 'No smoking for 2 hours before',
        desc: 'Avoid tobacco in the two hours leading up to the draw.',
        why: 'Nicotine briefly raises blood sugar and stress hormones around the time of the draw.',
        marker: 'e.g. Fasting Glucose',
      },
    ],
  },
  {
    node: { big: '✓', small: 'DRAW' },
    label: 'Before the sample draw',
    sub: 'One thing to tell our team.',
    items: [
      {
        title: 'List your medications',
        desc: 'Tell the phlebotomist about every medicine you take.',
        why: 'Some medicines change results; knowing them helps your doctor read your report correctly.',
        marker: 'e.g. PT / INR',
      },
    ],
  },
];

const PrepItem = ({ item }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      bgcolor: '#fff',
      border: '1px solid var(--border-l)',
      borderRadius: '11px',
      p: '13px 15px',
      mb: 1,
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: 22,
        height: 22,
        mt: '1px',
        borderRadius: '50%',
        bgcolor: 'rgba(61,122,82,0.10)',
        color: 'var(--sage)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CheckRoundedIcon sx={{ fontSize: 14 }} />
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: 'var(--ink)' }}>
        {item.title}
      </Typography>
      <Typography sx={{ color: 'var(--stone)', fontSize: 13 }}>{item.desc}</Typography>
      <Box
        sx={{
          mt: 0.75,
          fontSize: 12,
          color: 'var(--stone)',
          borderLeft: '2px solid var(--amber)',
          pl: 1.25,
          lineHeight: 1.45,
        }}
      >
        <Box
          component="span"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--amber)',
            fontSize: 11,
            mr: 0.75,
          }}
        >
          Why
        </Box>
        {item.why}
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            ml: 0.875,
            bgcolor: 'rgba(61,122,82,0.10)',
            color: 'var(--sage)',
            borderRadius: '999px',
            px: 1.125,
            py: '1px',
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {item.marker}
        </Box>
      </Box>
    </Box>
  </Box>
);

const PrepChecklist = ({ acknowledged, onAcknowledgeChange }) => (
  <Box>
    <Typography className="serif" variant="h3" sx={{ mb: 1 }}>
      How to prepare for your blood test
    </Typography>
    <Typography sx={{ fontSize: 13, color: 'var(--stone)', mb: 3, fontWeight: 300 }}>
      Please read these instructions before continuing. Following them keeps your sample clean
      and your results accurate — from three days out to the moment of your draw.
    </Typography>

    {/* Timeline */}
    <Box sx={{ position: 'relative' }}>
      {PHASES.map((phase, pi) => {
        const isLast = pi === PHASES.length - 1;
        return (
          <Box key={phase.label} sx={{ position: 'relative', pl: '70px', pb: isLast ? 0 : 3.5 }}>
            {/* Connector rail */}
            {!isLast && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '27px',
                  top: 8,
                  bottom: -8,
                  width: '2px',
                  background: 'linear-gradient(180deg, var(--amber) 0%, var(--sage) 100%)',
                }}
              />
            )}
            {/* Node */}
            <Box
              sx={{
                position: 'absolute',
                left: 8,
                top: 2,
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'var(--forest)',
                color: 'var(--sage-p)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 0.85,
                boxShadow: '0 0 0 5px var(--cream)',
              }}
            >
              <Box component="b" sx={{ fontSize: 15, color: '#fff', fontWeight: 700 }}>
                {phase.node.big}
              </Box>
              <Box component="em" sx={{ fontStyle: 'normal', fontSize: 8, letterSpacing: '0.5px' }}>
                {phase.node.small}
              </Box>
            </Box>

            <Typography
              className="serif"
              sx={{ fontWeight: 600, fontSize: 19, color: 'var(--forest)', mb: 0.25 }}
            >
              {phase.label}
            </Typography>
            <Typography sx={{ color: 'var(--stone)', fontSize: 13, mb: 1.75 }}>
              {phase.sub}
            </Typography>

            {phase.items.map((item) => (
              <PrepItem key={item.title} item={item} />
            ))}
          </Box>
        );
      })}
    </Box>

    {/* Advisory strip */}
    <Box
      sx={{
        bgcolor: 'var(--forest)',
        color: '#EAF1EC',
        borderRadius: '14px',
        p: '20px 22px',
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        mt: 1,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 38,
          height: 38,
          borderRadius: '9px',
          bgcolor: 'rgba(201,125,8,0.18)',
          color: 'var(--amber)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <WarningAmberRoundedIcon sx={{ fontSize: 20 }} />
      </Box>
      <Box>
        <Typography
          className="serif"
          sx={{ fontWeight: 600, fontSize: 16, color: 'var(--sage-p)', mb: 0.5 }}
        >
          Two things worth knowing
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: '#C7D6CC' }}>
          <Box component="b" sx={{ color: '#fff', fontWeight: 600 }}>
            Reschedule if you're unwell.
          </Box>{' '}
          Illness can skew results — book again once you've recovered.{' '}
          <Box component="b" sx={{ color: '#fff', fontWeight: 600 }}>
            Repeating a test?
          </Box>{' '}
          Try to use the same time of day each time, so the comparison is fair.
        </Typography>
      </Box>
    </Box>

    {/* Acknowledgement */}
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid var(--border-l)',
        borderRadius: '16px',
        p: '22px 24px',
        mt: 3,
        boxShadow: '0 18px 40px -26px rgba(21,42,30,0.4)',
      }}
    >
      <Box
        onClick={() => onAcknowledgeChange(!acknowledged)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 22,
            height: 22,
            mt: '1px',
            border: '2px solid var(--sage)',
            borderRadius: '6px',
            bgcolor: acknowledged ? 'var(--forest)' : 'transparent',
            borderColor: acknowledged ? 'var(--forest)' : 'var(--sage)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.18s',
          }}
        >
          <CheckRoundedIcon
            sx={{
              fontSize: 15,
              color: '#fff',
              opacity: acknowledged ? 1 : 0,
              transform: acknowledged ? 'scale(1)' : 'scale(0.5)',
              transition: 'all 0.18s',
            }}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}>
            I have read and understood the preparation instructions.
          </Typography>
          <Typography sx={{ fontSize: 12.5, fontWeight: 400, color: 'var(--stone)', mt: 0.25 }}>
            You can still review this page before your appointment.
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

export default PrepChecklist;
